import React, { useState, useEffect } from 'react';
import classNames from 'classnames';

import { FormattedMessage } from '../../../../util/reactIntl';
import { ACCOUNT_SETTINGS_PAGES } from '../../../../routing/routeConfiguration';
import {
  Avatar,
  InlineTextButton,
  LinkedLogo,
  Menu,
  MenuLabel,
  MenuContent,
  MenuItem,
  NamedLink,
} from '../../../../components';

import TopbarSearchForm from '../TopbarSearchForm/TopbarSearchForm';
import CustomLinksMenu from './CustomLinksMenu/CustomLinksMenu';

import css from './TopbarDesktop.module.css';
import MegaMenu from './components/MegaMenu/MegaMenu';
import { getUserTypePreference } from '../../../../util/dataExtractor';
import { UserTypePreference } from '../../../../util/enums';
import CartLink from '../CartLink/CartLink';

const SignupLink = () => {
  return (
    <NamedLink name="SignupPage" className={css.topbarLink}>
      <span className={css.topbarLinkLabel}>
        <FormattedMessage id="TopbarDesktop.signup" />
      </span>
    </NamedLink>
  );
};

const LoginLink = () => {
  return (
    <NamedLink name="LoginPage" className={css.topbarLink}>
      <span className={css.topbarLinkLabel}>
        <FormattedMessage id="TopbarDesktop.login" />
      </span>
    </NamedLink>
  );
};

const InboxLink = ({ notificationCount, inboxTab }) => {
  const notificationDot = notificationCount > 0 ? <div className={css.notificationDot} /> : null;
  return (
    <NamedLink className={css.topbarLink} name="InboxPage" params={{ tab: inboxTab }}>
      <FormattedMessage id="TopbarDesktop.inbox" />
      {notificationDot}
    </NamedLink>
  );
};

const ProfileMenu = ({ currentPage, currentUser, onLogout, showManageListingsLink, notificationCount }) => {
  const currentPageClass = page => {
    const isAccountSettingsPage =
      page === 'AccountSettingsPage' && ACCOUNT_SETTINGS_PAGES.includes(currentPage);
    return currentPage === page || isAccountSettingsPage ? css.currentPage : null;
  };

  const userType = getUserTypePreference(currentUser);
  const isSeller = userType === UserTypePreference.SELLER;

  return (
    <Menu>
      <MenuLabel className={css.profileMenuLabel} isOpenClassName={css.profileMenuIsOpen}>
        <Avatar className={css.avatar} user={currentUser} disableProfileLink />
      </MenuLabel>
      <MenuContent className={css.profileMenuContent}>
        <MenuItem key="OrdersListPage">
          <NamedLink
            className={classNames(css.menuLink, currentPageClass('OrdersListPage'))}
            name="OrdersListPage"
            params={{ tab: 'sales' }}
          >
            <span
              className={classNames(
                css.menuItemTextWithDot,
                notificationCount > 0 && css.hasNotifications
              )}
            >
              <FormattedMessage id="TopbarDesktop.OrdersListPage" />
              <span className={css.notificationDotMenu} />
            </span>
          </NamedLink>
        </MenuItem>

        <MenuItem key="ShopSettingsPage">
          <NamedLink
            className={classNames(css.menuLink, currentPageClass('ShopSettingsPage'))}
            name="ShopSettingsPage"
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.shopSettingsLink" />
          </NamedLink>{' '}
        </MenuItem>
        <MenuItem key="ShopifyAccountPage">
          <NamedLink
            className={classNames(css.menuLink, currentPageClass('ShopifyAccountPage'))}
            name="ShopifyAccountPage"
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.connectStore" />
          </NamedLink>{' '}
        </MenuItem>
        {/* <MenuItem key="ProfileSettingsPage">
          <NamedLink
            className={classNames(css.menuLink, currentPageClass('ProfileSettingsPage'))}
            name="ProfileSettingsPage"
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.profileSettingsLink" />
          </NamedLink>{' '}
        </MenuItem> */}

        <MenuItem key="AccountSettingsPage">
          <NamedLink
            className={classNames(css.menuLink, currentPageClass('AccountSettingsPage'))}
            name="AccountSettingsPage"
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.accountSettingsLink" />
          </NamedLink>
        </MenuItem>
        <MenuItem key="FavoritesPage">
          <NamedLink
            className={classNames(css.menuLink, currentPageClass('FavoritesPage'))}
            name="FavoritesPage"
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.favoritesPageLink" />
          </NamedLink>
        </MenuItem>
        <MenuItem key="WalletDashboardPage">
          <NamedLink
            className={classNames(css.menuLink, currentPageClass('WalletDashboardPage'))}
            name="WalletDashboardPage"
          >
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.walletDashboardPageLink" />
          </NamedLink>
        </MenuItem>
        <MenuItem key="logout">
          <InlineTextButton rootClassName={css.logoutButton} onClick={onLogout}>
            <span className={css.menuItemBorder} />
            <FormattedMessage id="TopbarDesktop.logout" />
          </InlineTextButton>
        </MenuItem>
      </MenuContent>
    </Menu>
  );
};

/**
 * Topbar for desktop layout
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {CurrentUser} props.currentUser API entity
 * @param {string?} props.currentPage
 * @param {boolean} props.isAuthenticated
 * @param {number} props.notificationCount
 * @param {Function} props.onLogout
 * @param {Function} props.onSearchSubmit
 * @param {Object?} props.initialSearchFormValues
 * @param {Object} props.intl
 * @param {Object} props.config
 * @param {boolean} props.showSearchForm
 * @param {boolean} props.showCreateListingsLink
 * @param {string} props.inboxTab
 * @returns {JSX.Element} search icon
 */
const TopbarDesktop = props => {
  const {
    className,
    config,
    customLinks,
    currentUser,
    currentPage,
    rootClassName,
    notificationCount = 0,
    intl,
    isAuthenticated,
    onLogout,
    onSearchSubmit,
    initialSearchFormValues = {},
    showSearchForm,
    showCreateListingsLink,
    inboxTab,
    viewport,
  } = props;
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const marketplaceName = config.marketplaceName;
  const authenticatedOnClientSide = mounted && isAuthenticated;
  const isAuthenticatedOrJustHydrated = isAuthenticated || !mounted;
  const isMobileLayout = viewport?.width && viewport?.width > 0 && viewport?.width < 768;
  const giveSpaceForSearch = customLinks == null || customLinks?.length === 0;
  const classes = classNames(rootClassName || css.root, className);
  const inboxLinkMaybe = authenticatedOnClientSide ? (
    <InboxLink notificationCount={notificationCount} inboxTab={inboxTab} />
  ) : null;

  const profileMenuMaybe = authenticatedOnClientSide ? (
    <ProfileMenu
      currentPage={currentPage}
      currentUser={currentUser}
      onLogout={onLogout}
      showManageListingsLink={showCreateListingsLink}
      notificationCount={notificationCount}
    />
  ) : null;

  const signupLinkMaybe = isAuthenticatedOrJustHydrated ? null : <SignupLink />;
  const loginLinkMaybe = isAuthenticatedOrJustHydrated ? null : <LoginLink />;

  const searchFormMaybe = showSearchForm ? (
    <TopbarSearchForm
      className={classNames(css.searchLink, { [css.takeAvailableSpace]: giveSpaceForSearch })}
      desktopInputRoot={css.topbarSearchWithLeftPadding}
      onSubmit={onSearchSubmit}
      initialValues={initialSearchFormValues}
      appConfig={config}
    />
  ) : (
    <div
      className={classNames(css.spacer, css.topbarSearchWithLeftPadding, {
        [css.takeAvailableSpace]: giveSpaceForSearch,
      })}
    />
  );
  const links = [
    // { text: 'Help Center', name: 'LandingPage' },
    { text: 'Trust & Safety', name: 'TrustAndSafetyPage' },
    // { text: 'Value Guide', name: 'LandingPage' },
    { text: 'How it works', name: 'HowItWorksPage' },
    { text: 'FAQ’s', name: 'LandingPage' },
    // { text: 'Boot blog', name: 'LandingPage' },
    // { text: 'Sync shopify', name: 'ShopifyAccountPage' },
  ];
  return (
    <nav className={classes}>
      <div className={css.mainTopHeader}>
        <div className={css.fixedWidthContainer}>
          <div className={css.leftNav}>
            <LinkedLogo
              className={css.logoLink}
              layout="desktop"
              alt={intl.formatMessage({ id: 'TopbarDesktop.logo' }, { marketplaceName })}
              linkToExternalSite={config?.topbar?.logoLink}
            />
          </div>
          <div className={css.middleNav}>{searchFormMaybe}</div>

          <div className={css.rightNav}>
            <CustomLinksMenu
              currentPage={currentPage}
              customLinks={customLinks}
              intl={intl}
              hasClientSideContentReady={
                authenticatedOnClientSide || !isAuthenticatedOrJustHydrated
              }
              showCreateListingsLink={showCreateListingsLink}
            />
            {inboxLinkMaybe}
            <CartLink />
            {profileMenuMaybe}
            {signupLinkMaybe}
            {loginLinkMaybe}
          </div>
        </div>
      </div>
      <div className={css.subHeader}>
        <div className={css.fixedWidthContainer}>
          <MegaMenu viewport={viewport} />
          <div className={css.otherLinks}>
            {links.map(({ text, name }) => (
              <NamedLink key={text} name={name} className={css.topbarLink}>
                {text}
              </NamedLink>
            ))}
          </div>
        </div>
      </div>
    </nav>
  );
};

export default TopbarDesktop;
